import { ArtifactActionsFooter } from "@/components/artifacts/ArtifactActionsFooter.tsx";
import { ArtifactErrorBoundary } from "@/components/artifacts/ArtifactErrorBoundary.tsx";
import { ArtifactLightbox } from "@/components/artifacts/ArtifactLightbox.tsx";
import {
  ArtifactShareControl,
  canShareArtifact,
} from "@/components/artifacts/ArtifactShareControl.tsx";
import { iconForName } from "@/components/artifacts/iconForName.ts";
import type { ArtifactAction, HeavyArtifactResponse } from "@/types/artifacts.ts";
import { artifactExpandedTitle } from "@/shared/artifactTitle.ts";
import { useIsSharedThread } from "@/hooks/useIsSharedThread.ts";
import { useGateValue } from "@statsig/react-bindings";
import type { ReactNode } from "react";

/**
 * Shared "expanded artifact" lightbox used by both surfaces that open a heavy
 * artifact full-screen: the omnibox-overlay biscuit's summary tap
 * (`ArtifactBiscuit`) and the in-thread chip tap (`HeavyArtifactItem`). Owns:
 *
 *   - The `<ArtifactLightbox>` shell (icon, title, dialog chrome)
 *   - The error-boundary wrap around the body
 *   - The footer: status-aware date on the left, `<ArtifactActionsButtons>`
 *     on the right when the artifact is waiting for user input
 *   - Optimistic in-progress flip on action fire + invalidation rollback on
 *     error, so the thread state updates immediately rather than waiting for
 *     the POST to round-trip
 *
 * The body is passed as `children` so each surface can render whatever it
 * needs (the chip mounts the legacy per-type fallback + dev toggle via
 * `ArtifactContent`; the biscuit just mounts `<ArtifactView>`).
 *
 * The parent owns `open` / `onOpenChange` so it can layer in surface-specific
 * side-effects (the chip refetches the artifact and pings the native webview
 * bridge in its handler; the biscuit just toggles local state).
 */
export const HeavyArtifactLightbox = ({
  artifact,
  threadId,
  messageId,
  open,
  onOpenChange,
  onImmediateClose,
  closeArtifactGroup,
  onStage,
  stagedAction,
  hideHeader,
  hideFooter,
  headerActions,
  children,
}: {
  artifact: HeavyArtifactResponse;
  threadId: number;
  messageId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Under a live review queue: stage the action into the batch instead of
   *  firing it, so acting from the fullscreen view batches the same way the
   *  card behind it does. The stage handler moves the review along. */
  onStage?: (action: ArtifactAction) => void;
  /** The action already staged for this artifact, if any. */
  stagedAction?: ArtifactAction;
  /** Synchronous "close right now" used when an action fires — the chip's
   *  `onOpenChange` is async (awaits a refetch + native bridge before
   *  flipping state), which would leave the dialog visibly open for the
   *  network round-trip after the user clicks an action. Surfaces with a
   *  sync `onOpenChange` (biscuit) don't need to pass this. */
  onImmediateClose?: () => void;
  /** Optional cleanup hook fired after an action — chip uses it to collapse
   *  its containing artifact group; biscuit doesn't need it. */
  closeArtifactGroup?: () => void;
  hideHeader?: boolean;
  hideFooter?: boolean;
  /** Controls in the header, just before the minimize button. A surface that
   *  shows the artifact away from its thread (the Library) puts the way back
   *  to that thread here; the in-thread surfaces leave it empty. */
  headerActions?: ReactNode;
  children: ReactNode;
}) => {
  const userIsAdmin = useGateValue("internal-features");
  const isSharedThread = useIsSharedThread();

  const Icon = iconForName(artifact.iconName);

  return (
    <ArtifactLightbox
      open={open}
      onOpenChange={onOpenChange}
      icon={Icon}
      title={artifactExpandedTitle(artifact)}
      hideHeader={hideHeader}
      hideFooter={hideFooter}
      headerActions={headerActions}
      footerSlot={
        <>
          <ArtifactActionsFooter
            artifact={artifact}
            threadId={threadId}
            onFired={() => {
              if (onImmediateClose) onImmediateClose();
              else onOpenChange(false);
              closeArtifactGroup?.();
            }}
            onStage={
              onStage &&
              ((action) => {
                // Close on the stage: the dialog is a way to read this one card
                // closely, and the decision it was opened for has been made. The
                // review behind it has already moved to the next draft.
                if (onImmediateClose) onImmediateClose();
                else onOpenChange(false);
                closeArtifactGroup?.();
                onStage(action);
              })
            }
            stagedAction={stagedAction}
          />
          {canShareArtifact(artifact, { isSharedThread }) && (
            <ArtifactShareControl
              artifactId={artifact.id}
              artifactType={artifact.artifactType}
              title={artifactExpandedTitle(artifact)}
            />
          )}
        </>
      }
    >
      <ArtifactErrorBoundary
        artifact={artifact}
        threadId={threadId}
        messageId={messageId}
        userIsAdmin={userIsAdmin}
      >
        {children}
      </ArtifactErrorBoundary>
    </ArtifactLightbox>
  );
};
